import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import { z } from "zod";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { CreateDegreeMutation } from "../../../graphql/generated/graphql";

const modals = {
  addConfirmation: {
    title: "Success!",
    ariaLabel: "Degree Added Successfully Modal",
    warningText: "The degree has been added.",
    actionButtonText: "OK",
  },
  addError: {
    title: "Error!",
    ariaLabel: "Degree Addition Error Modal",
    warningText: "An error occurred while adding the degree. You may not have permission to add a degree.",
    actionButtonText: "CLOSE",
  },
};

export enum AddDegreeModalState {
  CLOSED = "CLOSED",
  ADD_CONFIRMATION = "ADD_CONFIRMATION",
  ADD_ERROR = "ADD_ERROR",
}

const degreeSchema = z.object({
  degreeName: z.string().min(1, "Degree name is required"),
});

type DegreeFormData = z.infer<typeof degreeSchema>;

interface AddDegreeModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: DegreeFormData) => Promise<CreateDegreeMutation>;
}

const AddDegreeModal: React.FC<AddDegreeModalProps> = ({ visible, onClose, onSubmit }) => {
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: AddDegreeModalState.CLOSED,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm<DegreeFormData>({
    resolver: zodResolver(degreeSchema),
    defaultValues: {
      degreeName: "",
    },
  });

  const handleFormSubmit = React.useCallback(
    async (formData: DegreeFormData) => {
      try {
        const response = await onSubmit(formData);
        const result = response?.createDegree;

        if (!result) {
          setModalState({ state: AddDegreeModalState.ADD_ERROR });
          return;
        }

        reset();
        onClose();
        if (result.success) {
          setModalState({ state: AddDegreeModalState.ADD_CONFIRMATION });
        } else {
          setModalState({ state: AddDegreeModalState.ADD_ERROR });
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setModalState({ state: AddDegreeModalState.ADD_ERROR });
      }
    },
    [onSubmit, onClose]
  );

  return (
    <>
      <CustomModal
        visible={visible}
        onRequestClose={onClose}
        title="Add a Degree"
        exitFill="#6B7280"
        ariaLabel="Add degree modal"
        style={{ width: isMedium ? "50%" : "90%" }}
        onShow={() => {}}
        showCloseButton={true}>
        <View style={styles.container}>
          <View style={styles.formGroup}>
            <CustomText text="Degree Name" size="s" textType="text" style={{ color: "#182230" }} weight="ultraBold" />
            <CustomSpacing size="s" type="vertical" />
            <Controller
              control={control}
              name="degreeName"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  testID="degree-name-input"
                  value={value}
                  onChangeText={onChange}
                  placeholder="Understanding Design Change Types"
                  style={styles.input}
                />
              )}
            />
            {errors?.degreeName?.message && (
              <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.degreeName?.message} />
            )}
          </View>

          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              onPress={onClose}
              styleType="secondary"
              size="m"
              style={styles.cancelButton}
              textStyle={styles.cancelButtonText}
              leftIcon={null}
              rightIcon={null}
              disabled={false}
              textProps={{}}
            />
            <CustomButton
              text="Add Degree"
              onPress={handleSubmit(handleFormSubmit)}
              styleType="primary"
              size="m"
              style={styles.addButton}
              textStyle={styles.addButtonText}
              leftIcon={null}
              rightIcon={null}
              disabled={false}
              textProps={{}}
            />
          </View>
        </View>
      </CustomModal>
      <NewCustomAxioActionModal
        visible={modalState.state === AddDegreeModalState.ADD_CONFIRMATION}
        onClose={() => setModalState({ state: AddDegreeModalState.CLOSED })}
        modalTexts={modals.addConfirmation}
        contextualText={getValues("degreeName")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === AddDegreeModalState.ADD_ERROR}
        onClose={() => setModalState({ state: AddDegreeModalState.CLOSED })}
        modalTexts={modals.addError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  input: {
    width: "100%",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  addButton: {
    // backgroundColor: "#4F46E5",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  addButtonText: {
    color: "#FFFFFF",
  },
});

export default AddDegreeModal;
